import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

const POINTS_ERROR = "Points must be between 1 and 10,000.";
const EMPTY_GROUPS = { sp: [], dp: [], tp: [] };

export default function MatkaChoicePana({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [enterPoints, setEnterPoints] = useState("");
  const [types, setTypes] = useState({
    sp: false,
    dp: false,
    tp: false,
  });
  const [leftDigit, setLeftDigit] = useState("");
  const [middleDigit, setMiddleDigit] = useState("");
  const [rightDigit, setRightDigit] = useState("");
  const [saved, setSaved] = useState(EMPTY_GROUPS);
  const [loading, setLoading] = useState(false);
  const [pointsError, setPointsError] = useState("");
  const [notice, setNotice] = useState("");
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });
  const requestId = useRef(0);
  const centerRef = useRef(null);
  const rightRef = useRef(null);
  const pointsRef = useRef(null);

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );
  const hasDigit = Boolean(leftDigit || middleDigit || rightDigit);
  const chips = useMemo(() => {
    const list = [];
    if (types.sp) saved.sp.forEach((pana) => list.push({ pana, series: "sp" }));
    if (types.dp) saved.dp.forEach((pana) => list.push({ pana, series: "dp" }));
    if (types.tp) saved.tp.forEach((pana) => list.push({ pana, series: "tp" }));
    return list;
  }, [saved, types]);

  useEffect(() => {
    if (!leftDigit && !middleDigit && !rightDigit) {
      requestId.current += 1;
      setSaved(EMPTY_GROUPS);
      setLoading(false);
      return undefined;
    }

    const digits = `${leftDigit || "x"}${middleDigit || "x"}${rightDigit || "x"}`;
    const id = requestId.current + 1;
    requestId.current = id;
    setSaved(EMPTY_GROUPS);
    setLoading(true);
    const timer = setTimeout(() => {
      fetchMatkaSuggestions("choice_pana", digits)
        .then((data) => {
          if (id !== requestId.current) return;
          setSaved({
            sp: Array.isArray(data?.sp) ? data.sp.map(String) : [],
            dp: Array.isArray(data?.dp) ? data.dp.map(String) : [],
            tp: Array.isArray(data?.tp) ? data.tp.map(String) : [],
          });
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setSaved(EMPTY_GROUPS);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [leftDigit, middleDigit, rightDigit]);

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(
        currentBids,
        nextGameType,
        (bid) => `${bid.panna}|${bid.series}`
      )
    );
  };

  const handleTypeChange = (type) => {
    setTypes((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleDigitChange = (setter, nextField) => (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 1);
    setter(next);
    if (next) nextField?.current?.focus();
  };

  const handlePointsChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 5);
    setEnterPoints(next);
    if (!next) {
      setPointsError("");
      return;
    }
    const value = parseInt(next, 10);
    setPointsError(value >= 1 && value <= 10000 ? "" : POINTS_ERROR);
  };

  const handleSuggestionTap = (pana) => {
    setLeftDigit(pana[0] || "");
    setMiddleDigit(pana[1] || "");
    setRightDigit(pana[2] || "");
  };

  const handleAddBid = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (loading) return;

    if (enterPoints.trim() === "") {
      setNotice("Please fill all fields");
      return;
    }
    if (!types.sp && !types.dp && !types.tp) {
      setNotice("Please select at least one option (SP, DP, or TP)");
      return;
    }
    if (!hasDigit) {
      setNotice("Please enter at least one digit");
      return;
    }

    const pointsValue = parseInt(enterPoints, 10);
    if (!Number.isInteger(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
      setPointsError(POINTS_ERROR);
      return;
    }
    if (!chips.length) {
      setNotice("No valid combinations found for the entered digits");
      return;
    }

    setBids((currentBids) => {
      const nextBids = [...currentBids];
      chips.forEach((chip, index) => {
        const existingIndex = nextBids.findIndex(
          (bid) =>
            bid.panna === chip.pana &&
            bid.series === chip.series &&
            bid.gameType === gameType
        );
        if (existingIndex >= 0) {
          nextBids[existingIndex] = {
            ...nextBids[existingIndex],
            points: Number(nextBids[existingIndex].points) + pointsValue,
          };
          return;
        }
        nextBids.push({
          id: Date.now() + index,
          panna: chip.pana,
          series: chip.series,
          points: pointsValue,
          gameType,
        });
      });
      return nextBids;
    });
    setLeftDigit("");
    setMiddleDigit("");
    setRightDigit("");
    setEnterPoints("");
    setPointsError("");
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-4">Choice Panna</h1>

          {/* Form: Row 1 - GAME TYPE and ENTER POINTS */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">GAME TYPE</label>
              <MatkaSessionSelect
                  betDirection={selectedGameType?.betDirection}
                  value={gameType}
                  onChange={handleGameTypeChange}
                  variant="bulk"
                />
            </div>

            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">ENTER POINTS</label>
              <input
                ref={pointsRef}
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="Enter Points (e.g. 100)"
                value={enterPoints}
                onChange={handlePointsChange}
              />
              {pointsError && <p className="matka-sangam-error">{pointsError}</p>}
            </div>
          </div>

          {/* Form: Row 2 - SP, DP, TP Toggle Pills */}
          <div className="matka-odd-even-toggles mb-3">
            <label className={`matka-odd-even-pill ${types.sp ? "active" : ""}`}>
              <input
                type="checkbox"
                checked={types.sp}
                onChange={() => handleTypeChange("sp")}
              />
              <span>SP</span>
            </label>

            <label className={`matka-odd-even-pill ${types.dp ? "active" : ""}`}>
              <input
                type="checkbox"
                checked={types.dp}
                onChange={() => handleTypeChange("dp")}
              />
              <span>DP</span>
            </label>

            <label className={`matka-odd-even-pill ${types.tp ? "active" : ""}`}>
              <input
                type="checkbox"
                checked={types.tp}
                onChange={() => handleTypeChange("tp")}
              />
              <span>TP</span>
            </label>
          </div>

          {/* Form: Row 3 - LEFT DIGIT, MIDDLE DIGIT, RIGHT DIGIT */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <label className="matka-panna-bulk-label">LEFT DIGIT</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="matka-panna-bulk-input"
                placeholder="1"
                value={leftDigit}
                onChange={handleDigitChange(setLeftDigit, centerRef)}
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="matka-panna-bulk-label">MIDDLE DIGIT</label>
              <input
                ref={centerRef}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="matka-panna-bulk-input"
                placeholder="5"
                value={middleDigit}
                onChange={handleDigitChange(setMiddleDigit, rightRef)}
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="matka-panna-bulk-label">RIGHT DIGIT</label>
              <input
                ref={rightRef}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="matka-panna-bulk-input"
                placeholder="9"
                value={rightDigit}
                onChange={handleDigitChange(setRightDigit, pointsRef)}
              />
            </div>
          </div>

          {(types.sp || types.dp || types.tp) && loading && (
            <p className="matka-pana-suggestion-status">
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-label="Loading"
              ></span>
            </p>
          )}
          {!loading && chips.length > 0 && (
            <div className="mb-3">
              <p className="matka-panna-bulk-label">Suggestions ({chips.length})</p>
              <div className="matka-pana-suggestions">
                {chips.map((chip) => (
                  <button
                    key={`${chip.series}-${chip.pana}`}
                    type="button"
                    className="matka-pana-suggestion-btn"
                    onClick={() => handleSuggestionTap(chip.pana)}
                  >
                    {chip.pana}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add Bid Button (Right-Aligned) */}
          <div className="d-flex justify-content-end mb-4">
            <button
              type="button"
              className="matka-add-bid-btn"
              onClick={handleAddBid}
              disabled={loading}
            >
              ADD BID
            </button>
          </div>

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Panna</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.panna}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.panna}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {notice && (
        <div className="matka-sangam-popup" role="dialog" aria-modal="true">
          <div className="matka-sangam-popup-box">
            <p>{notice}</p>
            <button type="button" onClick={() => setNotice("")}>
              OK
            </button>
          </div>
        </div>
      )}

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
